import { observable, action, makeObservable } from 'mobx';
import { a11yAnnounce } from 'fn-common-ui';
import { apiService } from '../services/api.service';

class UploadStore {

  constructor() {
    makeObservable(this);
  }

    @action registerDropzone(dropzone) {
        if (!this.dropzones[dropzone.id]) {
          const newDropzone = Object.assign({
              filename: null,
              isUploading: false,
              isDeleting: false,
              progress: 0,
              invalidUploadAttempted: []
          }, dropzone);
          this.dropzones = Object.assign({}, this.dropzones, {[dropzone.id]: newDropzone}); // strange work around to keep this.dropzones fully observable
        }
        return this.dropzones[dropzone.id];
    }

    @action deRegisterDropzone(dropzoneId) {
        delete this.dropzones[dropzoneId]
    }

    getFileMetaObject(id) {
        return this.dropzones[id]
    }

    @action handleImageUpload(event, id) {
        let fileMetaObject = this.getFileMetaObject(id);
        let files = this.getFilesFromUploadEvent(event);
        for (let file of files) {
            const fileIsValid = this.fileIsValidForUpload({ file, fileMetaObject })
            return new Promise((resolve, reject) => {
                const reader =  new window.FileReader();
                reader.onloadend = (event) => {
                    if (fileIsValid) {
                        let image = new Image();
                        image.src = event.target.result;
                        image.onload = () => {
                            if (image.width !== 464 || image.height !== 260) {
                                const errorMessage = 'Image must be 260px high by 464px wide'
                                fileMetaObject.invalidUploadAttempted.push(errorMessage);
                                reject(new Error(`Error using ${file.name}: ${errorMessage}` ))
                            } else {
                                resolve(this.uploadItem({
                                    file,
                                    fileMetaObject
                                }));
                            }
                        }
                    }
                }
                reader.onerror = (e) => { reject(new Error('Error reading' + file.name + ': ' + e.target.result)) }
                reader.readAsDataURL(file);
            })
        }
    }

    @action handleFileUpload(event, id) {
        let fileMetaObject = this.getFileMetaObject(id);
        let files = this.getFilesFromUploadEvent(event)
        return this.checkFileForErrorsAndUpload({
            uploadedFiles: files,
            fileMetaObject
        })
    }

    @action handleRemoveFile(id) {
        let fileMetaObject = this.getFileMetaObject(id);
        const fileKey = fileMetaObject.filename || fileMetaObject.imageUrl;
        this.pendingDeletes.push(fileKey);
        this.clearFileMetaObject(fileMetaObject)
    }

    @action announceSuccess(actionWord) {
        a11yAnnounce({
            message: `${actionWord} complete.`,
            messageType: 'status'
        });
    }

    @action announceFailure(actionWord, errorMessage = '') {
        a11yAnnounce({
            message: `${actionWord} failed. ${errorMessage}`,
            messageType: 'status'
        });
    }

    @action getFilesFromUploadEvent = (event) => {
        let files = [];
        if (event.target.type === 'file') {
            files = Array.from(event.target.files);
        } else if (event.dataTransfer.files) {
            files = Array.from(event.dataTransfer.files);
        } else if (event.dataTransfer.items) {
            files = Array.from(event.dataTransfer.items).map(item => item.getAsFile());
        }
        return files;
    }

    @action fileIsValidForUpload({ file, fileMetaObject }) {
        var allowedExtensions = /(\.mp4|\.pdf|\.vtt|\.png)$/i;

      const extensionIsAllowed = fileMetaObject.allowedExtensions.filter(ext => {
        return file.name.toLowerCase().includes(ext)
      }).length > 0 && allowedExtensions.exec(file.name);
      const allowedExtensionsString = fileMetaObject.allowedExtensions.map(ext => ext.toUpperCase()).join(' and ');
      if (!extensionIsAllowed) {
          fileMetaObject.invalidUploadAttempted.push(`Invalid file type. ${allowedExtensionsString} files only.`);
          return false;
      } else if (file.size > 104857600) {
          fileMetaObject.invalidUploadAttempted.push('File size exceeds 100MB.');
          return false;
      } else {
          return true;
      }
    }

    @action checkFileForErrorsAndUpload({ uploadedFiles, fileMetaObject }) {
        for (let file of uploadedFiles) {
            const fileIsValid = this.fileIsValidForUpload({ file, fileMetaObject })
            return new Promise((resolve, reject) => {
                const reader =  new window.FileReader();
                reader.onload = () => {
                    if (fileIsValid) {
                        resolve(
                            this.uploadItem({
                                file,
                                fileMetaObject
                            })
                        )
                    }
                }
                reader.onerror = (e) => { reject(new Error('Error reading' + file.name + ': ' + e.target.result)) }
                reader.readAsDataURL(file);
            })
        }
    }

    @action uploadItem({ file, fileMetaObject }) {
        const success = (response) => {
            this.announceSuccess('Upload');
            fileMetaObject.filename = file.name;
            fileMetaObject.imageUrl = response.url;
            fileMetaObject.uploadDate = Date.now();
            fileMetaObject.isUploading = false;
            this.newUploads.push(fileMetaObject)
            return response;
        }
        const fail = (err) => {
            this.announceFailure('Upload');
            fileMetaObject.isUploading = false;
            throw err;
        }

        fileMetaObject.invalidUploadAttempted = [];
        fileMetaObject.isUploading = true;
        fileMetaObject.progress = 0;

        return apiService.uploadFile(file, fileMetaObject).then(success, fail);
    }

    @action clearFileMetaObject(fileMetaObject) {
        fileMetaObject.filename = null;
        fileMetaObject.imageUrl = null;
        fileMetaObject.isUploading = false;
        fileMetaObject.progress = 0;
    }

    @action removeUnusedUploads() {
        this.newUploads.forEach(fileMetaObject => {
            // do transparently in the background
            apiService.deleteFile(fileMetaObject.filename)
            this.clearFileMetaObject(fileMetaObject)
        })
        this.newUploads = [];
    }

    @action fullyDeleteRemovedFiles() {
        this.pendingDeletes.forEach(fileKey => {
            // do transparently in the background
            apiService.deleteFile(fileKey)
        })
        this.pendingDeletes = [];
    }

    @observable dropzones = {};
    newUploads = [];
    pendingDeletes = [];

}

export const uploadStore = new UploadStore();
